import { useCallback, useMemo, useRef, useState } from 'react';
import { Space, Select } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import 'common/components/ag-grid/registerAgGridModules';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { FormButton, FormDeleteButton } from 'common/components';
import { UpcSelectCellEditor } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useBeverageGridColumns';
import {
  createCellErrorStyle,
  ErrorCellRenderer,
  HeaderGrid,
  NumberCellEditor,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useOnlineCoreGridColumns';

import useWizard from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';
import { runAsyncValidator } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/validation';
import { createUpc12Validator } from 'common/components/product-add/components/utils/validation';

// ---------------------------------------------------------------------------
// Item UPC validation (shared logic with ShipperDetailCellRenderer)
// ---------------------------------------------------------------------------

const validateItemUpc = async (upc) => {
  if (!upc?.trim()) return 'Unit UPC is required.';
  const msg = await runAsyncValidator(createUpc12Validator, 'Unit UPC', upc);
  return msg ?? null;
};

// ---------------------------------------------------------------------------
// Custom expand/collapse cell renderer
// ---------------------------------------------------------------------------

const ExpandCellRenderer = (props) => {
  const { node } = props;
  const [expanded, setExpanded] = useState(node.expanded);

  const handleClick = (e) => {
    e.stopPropagation();
    const next = !node.expanded;
    node.setExpanded(next);
    setExpanded(next);
  };

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
        color: '#1890ff',
        fontSize: 12,
      }}
    >
      {expanded ? <DownOutlined /> : <RightOutlined />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dedicated expand column
// ---------------------------------------------------------------------------

const EXPAND_COLUMN = {
  colId: 'expand',
  headerName: '',
  width: 40,
  minWidth: 40,
  maxWidth: 40,
  resizable: false,
  sortable: false,
  editable: false,
  suppressHeaderMenuButton: true,
  cellRenderer: ExpandCellRenderer,
  cellStyle: { padding: 0 },
  pinned: 'left',
};

// ---------------------------------------------------------------------------
// UPC Select cell editor for multipack item rows
// ---------------------------------------------------------------------------

const MultipackItemUpcCellEditor = (props) => {
  const {
    initialValue,
    onValueChange,
    stopEditing,
    coreDataOptions = [],
  } = props;
  const [value, setValue] = useState(initialValue ?? null);

  const updateValue = (val) => {
    setValue(val);
    onValueChange(val);
  };

  return (
    <Select
      autoFocus
      showSearch
      defaultOpen
      value={value}
      style={{ width: '300px' }}
      dropdownStyle={{ minWidth: 360 }}
      placeholder='Select UPC'
      filterOption={(input, option) =>
        String(option?.label ?? '')
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      onChange={updateValue}
      onSelect={(val) => {
        updateValue(val);
        setTimeout(() => stopEditing(), 0);
      }}
      onBlur={() => stopEditing()}
    >
      {coreDataOptions.map(({ upc, label }) => (
        <Select.Option key={upc} value={upc} label={label}>
          {label}
        </Select.Option>
      ))}
    </Select>
  );
};
MultipackItemUpcCellEditor.displayName = 'MultipackItemUpcCellEditor';

// ---------------------------------------------------------------------------
// Detail item column defs factory
// ---------------------------------------------------------------------------

const createDetailItemColumnDefs = (coreDataOptions) => [
  {
    field: 'itemUpc',
    headerComponent: HeaderGrid,
    headerComponentParams: {
      label: 'UPC for Item(s) contained in Multipack',
      tooltip:
        'Select the UPC of each unit item contained inside this Multipack. Only non-multipack products from Core Data are available for selection.',
      required: true,
    },
    width: 400,
    editable: true,
    resizable: true,
    singleClickEdit: true,
    cellEditor: MultipackItemUpcCellEditor,
    cellEditorParams: { coreDataOptions },
    cellStyle: createCellErrorStyle('itemUpc'),
    cellRenderer: ErrorCellRenderer,
  },
  {
    field: 'itemDescription',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Item Description' },
    flex: 1,
    editable: true,
    resizable: true,
    singleClickEdit: true,
  },
  {
    field: 'itemCount',
    cellDataType: 'number',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Item Count' },
    width: 130,
    editable: true,
    resizable: true,
    singleClickEdit: true,
    cellEditor: NumberCellEditor,
  },
];

// ---------------------------------------------------------------------------
// MultipackDetailCellRenderer
// Mirrors ShipperDetailCellRenderer — reads/writes multipackItems in wizard state
// ---------------------------------------------------------------------------

const DETAIL_DEFAULT_COL_DEF = {
  sortable: false,
  resizable: true,
  filter: false,
};

export const MultipackDetailCellRenderer = (props) => {
  const { data } = props; // parent row — used only for uniqueId lookup
  const detailGridRef = useRef(null);

  const { state, actions } = useWizard();
  const [selectedItem, setSelectedItem] = useState(null);

  const [recaptureVisible, setRecaptureVisible] = useState(false);
  const [recaptureTargetIndex, setRecaptureTargetIndex] = useState(null);

  // Always read from live wizard state, never from stale props.data
  const liveProduct = useMemo(
    () => state.products.find((p) => p.uniqueId === data.uniqueId),
    [state.products, data.uniqueId]
  );

  const items = useMemo(
    () => liveProduct?.multipackItems ?? [],
    [liveProduct?.multipackItems]
  );

  // Build UPC options from non-multipack core products, excluding already-claimed UPCs
  const coreDataOptions = useMemo(() => {
    const claimedUpcs = new Set(
      items.map((item) => item.itemUpc).filter(Boolean)
    );

    return state.products
      .filter((p) => !p.multipack && p.unitUpc && !claimedUpcs.has(p.unitUpc))
      .map((p) => ({
        upc: p.unitUpc,
        label: p.productName ? `${p.unitUpc} — ${p.productName}` : p.unitUpc,
        productName: p.productName ?? '',
      }));
  }, [state.products, items]);

  // ── Persist items back to wizard state ──────────────────────────────────
  const persistItems = useCallback(
    (nextItems) => {
      const totalUnits = nextItems.reduce(
        (sum, item) => sum + (Number(item.itemCount) || 0),
        0
      );

      actions.setProducts(
        state.products.map((p) =>
          p.uniqueId === data.uniqueId
            ? {
                ...p,
                multipackItems: nextItems,
                multipackTotalUnit: totalUnits || null,
              }
            : p
        )
      );
    },
    [actions, state.products, data.uniqueId]
  );

  // ── Add a new empty item row ─────────────────────────────────────────────
  const handleAddItem = useCallback(async () => {
    const newItem = { itemUpc: '', itemDescription: '', itemCount: null };

    const error = await validateItemUpc('');
    newItem.validationErrors = error
      ? [{ field: 'itemUpc', message: error, messageType: 'Error' }]
      : [];

    const next = [...items, newItem];
    persistItems(next);

    setTimeout(() => {
      const api = detailGridRef.current?.api;
      if (!api) return;
      api.startEditingCell({ rowIndex: next.length - 1, colKey: 'itemUpc' });
    }, 0);
  }, [items, persistItems]);

  // ── Remove selected item row ─────────────────────────────────────────────
  const handleRemoveItem = useCallback(() => {
    if (!selectedItem) return;
    persistItems(items.filter((item) => item !== selectedItem));
    setSelectedItem(null);
  }, [items, selectedItem, persistItems]);

  // ── Cell value changed — validate UPC and auto-fill description ──────────
  const handleItemCellValueChanged = useCallback(
    async (params) => {
      const next = await Promise.all(
        items.map(async (item, index) => {
          if (index !== params.rowIndex) return item;

          const updatedItem = {
            ...item,
            [params.colDef.field]: params.newValue,
          };

          if (params.colDef.field === 'itemUpc') {
            const error = await validateItemUpc(params.newValue);
            updatedItem.validationErrors = error
              ? [{ field: 'itemUpc', message: error, messageType: 'Error' }]
              : [];

            const matched = coreDataOptions.find(
              (opt) => opt.upc === params.newValue
            );
            if (matched && !item.itemDescription) {
              updatedItem.itemDescription = matched.productName;
            }
          }

          return updatedItem;
        })
      );

      persistItems(next);
    },
    [items, persistItems, coreDataOptions]
  );

  const onSelectionChanged = useCallback(() => {
    const selected = detailGridRef.current?.api?.getSelectedRows();
    setSelectedItem(selected?.[0] ?? null);
  }, []);

  const detailColumnDefs = useMemo(
    () => createDetailItemColumnDefs(coreDataOptions),
    [coreDataOptions]
  );

  return (
    <div
      style={{
        padding: '8px 12px',
        background: '#fafafa',
        borderTop: '1px solid #e8e8e8',
      }}
    >
      {/* Toolbar */}
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}
      >
        <Space size={8}>
          <FormButton
            icon={<PlusOutlined />}
            onClick={handleAddItem}
            text='Add Item'
            type='secondary'
          />
          <FormDeleteButton
            danger
            icon={<DeleteOutlined />}
            disabled={!selectedItem}
            onClick={handleRemoveItem}
            text='Remove'
          />
        </Space>
      </div>

      {/* Detail grid */}
      <div className='ag-theme-alpine' style={{ width: '100%', height: 204 }}>
        <AgGridReact
          theme="legacy"
          ref={detailGridRef}
          columnDefs={detailColumnDefs}
          rowData={items}
          headerHeight={32}
          rowHeight={34}
          rowSelection='single'
          defaultColDef={DETAIL_DEFAULT_COL_DEF}
          stopEditingWhenCellsLoseFocus
          singleClickEdit
          overlayNoRowsTemplate='No Rows to Show'
          onCellValueChanged={handleItemCellValueChanged}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Hidden columns for quick filter
// ---------------------------------------------------------------------------

const HIDDEN_FILTER_COLUMNS = [
  { field: 'vendorItemNumber', hide: true },
  { field: 'brandName', hide: true },
  { field: 'productName', hide: true },
  { field: 'productDescription', hide: true },
  { field: 'ecommerceDescription', hide: true },
  { field: 'size', hide: true },
  { field: 'sizeUom', hide: true },
  { field: 'innerPackUpc', hide: true },
  { field: 'innerPackGtin', hide: true },
  { field: 'innerPackPackSize', hide: true },
  { field: 'caseGtin', hide: true },
  { field: 'casePackSize', hide: true },
  { field: 'caseInnerPackCount', hide: true },
];

// ---------------------------------------------------------------------------
// Parent UPC column — fully editable with per-row exclusion (mirrors shipper)
// ---------------------------------------------------------------------------

const createUpcColumn = (unitUpcOptions, upcByUniqueId) => ({
  field: 'unitUpc',
  headerComponent: HeaderGrid,
  headerComponentParams: {
    label: 'Multipack UPC',
    required: true,
    tooltip:
      'The UPC of the Multipack product, synced automatically from Core Data.',
  },
  width: 200,
  editable: true,
  singleClickEdit: true,
  resizable: true,
  cellEditor: UpcSelectCellEditor,
  cellEditorPopup: true,
  cellRenderer: ErrorCellRenderer,
  cellStyle: createCellErrorStyle('unitUpc'),
  cellEditorParams: (params) => {
    const currentRowUniqueId = params.data?.uniqueId;
    const currentRowUpc = params.data?.unitUpc;

    const claimedByOthers = new Set();
    upcByUniqueId.forEach((upc, uniqueId) => {
      if (uniqueId !== currentRowUniqueId && upc) {
        claimedByOthers.add(upc);
      }
    });

    const availableOptions = unitUpcOptions.filter(
      ({ upc }) => !claimedByOthers.has(upc) || upc === currentRowUpc
    );

    return { availableOptions };
  },
});

// ---------------------------------------------------------------------------
// Hook — parent multipack grid columns
// ---------------------------------------------------------------------------

/**
 * Returns column definitions for the parent Multipack tab grid.
 * Each row expands via MultipackDetailCellRenderer to show its multipackItems.
 *
 * @param {{ upc: string, label: string }[]} unitUpcOptions
 * @param {object[]}                         products
 */
export const useMultipackGridColumns = (unitUpcOptions = [], products = []) => {
  const upcByUniqueId = useMemo(() => {
    const map = new Map();
    for (const product of products) {
      if (product.uniqueId) {
        map.set(product.uniqueId, product.unitUpc ?? null);
      }
    }
    return map;
  }, [products]);

  const columnDefs = useMemo(
    () => [
      EXPAND_COLUMN,
      createUpcColumn(unitUpcOptions, upcByUniqueId),
      {
        field: 'multipackDescription',
        headerComponent: HeaderGrid,
        headerComponentParams: { label: 'Multipack Description' },
        width: 280,
        flex: 1,
        editable: true,
        resizable: true,
      },
      {
        field: 'multipackTotalUnit',
        cellDataType: 'number',
        headerComponent: HeaderGrid,
        headerComponentParams: { label: 'Total Units' },
        width: 130,
        editable: true,
        resizable: true,
        cellEditor: NumberCellEditor,
      },
      ...HIDDEN_FILTER_COLUMNS,
    ],
    [unitUpcOptions, upcByUniqueId]
  );

  return { columnDefs };
};

export default useMultipackGridColumns;
